/**
 * Session state for the customer app.
 *
 * Boot: the access token is memory-only, so every cold start must
 * silently establish a session before rendering protected content. We do that
 * by calling GET /me — with no bearer it 401s, the @dp/api client transparently
 * runs the cookie refresh (single-flight) and replays; success means we had a
 * valid refresh cookie, failure means "logged out". No redirect loop: the boot
 * failure is swallowed and we simply render as unauthenticated.
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { getClient } from "../api/client.js";
import { setAuthFailureHandler } from "../api/client.js";
import { getMe, logout, type BrowserSession, type CustomerProfile } from "../api/auth.js";

export interface SessionCustomer {
  id: string;
  locale: string;
  /** An open string (active, frozen, closing, closed today): a value the app does not know passes through. */
  status: string;
}

type Phase = "booting" | "ready";

interface AuthContextValue {
  phase: Phase;
  customer: SessionCustomer | null;
  isAuthenticated: boolean;
  /** Store the access token + customer from a fresh session (login/register/reset). */
  establishSession: (session: BrowserSession) => void;
  /** Revoke the refresh cookie and clear memory state. */
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function toCustomer(p: CustomerProfile): SessionCustomer {
  return { id: p.id, locale: p.locale, status: p.status };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [phase, setPhase] = useState<Phase>("booting");
  const [customer, setCustomer] = useState<SessionCustomer | null>(null);

  useEffect(() => {
    let alive = true;

    // If a refresh ever fails mid-session, drop to unauthenticated.
    setAuthFailureHandler(() => {
      if (alive) setCustomer(null);
    });

    // Silent boot: /me → (401 → cookie refresh → replay) → profile, or logged out.
    getMe()
      .then((profile) => {
        if (alive) setCustomer(toCustomer(profile));
      })
      .catch(() => {
        /* not logged in — expected on a fresh device */
      })
      .finally(() => {
        if (alive) setPhase("ready");
      });

    return () => {
      alive = false;
    };
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      phase,
      customer,
      isAuthenticated: customer !== null,
      establishSession: (session) => {
        getClient().tokens.set(session.access_token);
        setCustomer({
          id: session.customer.id,
          locale: session.customer.locale,
          status: session.customer.status,
        });
      },
      signOut: async () => {
        try {
          await logout();
        } catch {
          /* best-effort; clear locally regardless */
        }
        getClient().tokens.set(null);
        setCustomer(null);
      },
    }),
    [phase, customer],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
